import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Bell, Check, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  clearNotification,
  clearAllNotifications,
} from '@/hooks/useTickets';
import type { NotificationItem } from '@uniticket/shared';

type Filter = 'all' | 'unread' | 'read' | 'cleared';
const PAGE_SIZE = 30;

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function paramsForFilter(filter: Filter): Record<string, string> {
  switch (filter) {
    case 'unread': return { isRead: 'false' };
    case 'read': return { isRead: 'true' };
    case 'cleared': return { cleared: 'true' };
    default: return {};
  }
}

export function NotificationsPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchNotifications({ limit: String(PAGE_SIZE), ...paramsForFilter(filter) });
      setItems(res.data);
      setCursor(res.meta.cursor);
      setHasMore(res.meta.hasMore);
    } catch {
      toast.error(t('notifications.loadError'));
    } finally {
      setLoading(false);
    }
  }, [filter, t]);

  useEffect(() => { load(); }, [load]);

  async function loadMore() {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const res = await fetchNotifications({ limit: String(PAGE_SIZE), cursor, ...paramsForFilter(filter) });
      setItems((prev) => [...prev, ...res.data]);
      setCursor(res.meta.cursor);
      setHasMore(res.meta.hasMore);
    } catch {
      toast.error(t('notifications.loadError'));
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleClick(n: NotificationItem) {
    if (!n.isRead) {
      try {
        await markNotificationRead(n.id);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
      } catch { /* ignore */ }
    }
    if (n.ticketId) navigate(`/tickets/${n.ticketId}`);
  }

  async function handleMarkAllRead() {
    try {
      await markAllNotificationsRead();
      setItems((prev) => prev.map((x) => ({ ...x, isRead: true })));
      // Unread filter no longer matches anything that was just read
      if (filter === 'unread') load();
    } catch {
      toast.error(t('notifications.markAllReadError'));
    }
  }

  async function handleClear(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    try {
      await clearNotification(id);
      // The cleared item leaves every view except "Cleared"
      if (filter !== 'cleared') setItems((prev) => prev.filter((x) => x.id !== id));
    } catch {
      toast.error(t('notifications.clearError'));
    }
  }

  async function handleClearAll() {
    try {
      await clearAllNotifications();
      if (filter !== 'cleared') setItems([]);
      else load();
    } catch {
      toast.error(t('notifications.clearError'));
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
            <Bell className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-lg font-bold text-slate-800">{t('notifications.title')}</h2>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={(v) => setFilter(v as Filter)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('notifications.filterAll')}</SelectItem>
              <SelectItem value="unread">{t('notifications.filterUnread')}</SelectItem>
              <SelectItem value="read">{t('notifications.filterRead')}</SelectItem>
              <SelectItem value="cleared">{t('notifications.filterCleared')}</SelectItem>
            </SelectContent>
          </Select>
          {filter !== 'cleared' && (
            <>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={handleMarkAllRead}>
                <Check className="w-4 h-4" />
                {t('notifications.markAllRead')}
              </Button>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={handleClearAll}>
                <Trash2 className="w-4 h-4" />
                {t('notifications.clearAll')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
          </div>
        ) : items.length === 0 ? (
          <p className="text-center text-slate-400 py-16">{t('notifications.empty')}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((n) => (
              <li key={n.id}>
                <div
                  onClick={() => handleClick(n)}
                  className={`group flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                    !n.isRead ? 'bg-brand-rose/5' : ''
                  }`}
                >
                  {!n.isRead ? (
                    <span className="mt-1.5 w-2 h-2 bg-brand-rose rounded-full shrink-0" />
                  ) : (
                    <span className="mt-1.5 w-2 h-2 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm leading-snug ${!n.isRead ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>
                      {n.title}
                    </p>
                    {n.body && <p className="text-xs text-slate-400 mt-0.5 truncate">{n.body}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      {n.ticketNumber && (
                        <span className="text-[10px] font-mono font-medium text-brand-rose">{n.ticketNumber}</span>
                      )}
                      <span className="text-[10px] text-slate-400">{timeAgo(n.createdAt)}</span>
                    </div>
                  </div>
                  {filter !== 'cleared' && (
                    <button
                      onClick={(e) => handleClear(e, n.id)}
                      title={t('notifications.clear')}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {!loading && hasMore && (
          <div className="p-3 border-t border-slate-100 text-center">
            <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? t('notifications.loading') : t('notifications.loadMore')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
